import Vue from 'vue';
import 'vue-phone-number-input/dist/vue-phone-number-input.css';
import { AttaServices, PhoneConfirmationService } from '@/services'
import { Component } from 'vue-property-decorator';
import { SentryPlugin } from '@/plugins'
import { LOCAL_STORAGE } from '@/utils/LSUtil'
import { User }  from '@/interfaces/IUser'
import * as PhoneIntf from '@/interfaces/PhoneConfirmation'
import VuePhoneNumberInput from 'vue-phone-number-input';
import { CaptureEvent, Events } from '@/services/AmplitudeService'

import './PhoneUpdateModal.scss'

import {
  VCard,
  VDialog,
  VCardTitle,
  VCardText,
  VTextField,
  VCheckbox,
  VBtn,
  VSpacer,
  VRow,
  VCol,
  VContainer
} from 'vuetify/lib';

export enum MODAL_STEP {
  PHONE_CONFIRMATION = 1,
  TOKEN_VALIDATION = 2
}

@Component({
  name: 'PhoneUpdateModal'
})
export class PhoneUpdateModal extends Vue {
  step: number = MODAL_STEP.PHONE_CONFIRMATION
  modalState: boolean = false
  sessionId: string | null = null

  phoneData = {
    phone: '',
    formattedNumber: '',
    nationalNumber: '',
    isValid: false
  }

  verificationPinCode: string[] = ['', '', '', '', '', '']
  smsConsent: boolean = false
  userConfirmedPhoneNumber: boolean = false

  async mounted() {
    const { dispatch, state } = this.$store
    await dispatch('getShouldUserUpdatePhone')
    this.modalState = state.app.shouldUpdatePhone

    if (this.modalState) {
      CaptureEvent(Events.PhoneUpdateModalView)
    }
  }

  get canSubmitPhoneConfirmationStep(): boolean {
    return this.userConfirmedPhoneNumber && this.smsConsent &&
           this.phoneData.isValid && this.phoneData.phone.length > 13;
  }

  get canSubmitTokenValidationStep(): boolean {
    return this.verificationPinCode.every(digit => digit);
  }

  async handleSubmit(e: Event) {
    e.preventDefault();
    if (this.step === MODAL_STEP.PHONE_CONFIRMATION) {
      if (!this.canSubmitPhoneConfirmationStep) return;
      if (await this.handlePhoneConfirmationStart()){
        this.step = MODAL_STEP.TOKEN_VALIDATION;
      } else {
        this.presentSnackBar("Ocorreu um erro. Tente novamente.");
      }
    } else {
      if (!this.canSubmitTokenValidationStep) return;
      if (await this.handlePinCodeValidation()) {
        if(await this.handlePhoneUpdate()) {
          this.modalState = false
          this.presentSnackBar('Número de telefone verificado com sucesso!')
          CaptureEvent(Events.PhoneUpdateCompleted)
        } else {
          this.presentSnackBar("Oops! Houve um erro na atualização do telefone.");
        }
      } else {
        this.presentSnackBar("Código inválido. Tente novamente.");
      }
    }
  }

  presentSnackBar(message: string) {
    const { state } = this.$store;
    state.app.snackbar.text = message;
    state.app.snackbar.value = true;
  }

  async handlePhoneUpdate() {
    const request = new User.UserPhoneUpdate();
    request.mobilePhoneDdd = parseInt(this.phoneData.nationalNumber.slice(0, 2), 10);
    request.mobilePhoneNumber = this.phoneData.nationalNumber.slice(2).replace(/\D/g, '');

    SentryPlugin.addBreadcrumb({
      category: 'Phone Update Modal',
      message: 'Updating phone',
      data: {
        phoneDdd: request.mobilePhoneDdd,
        phoneNumber: request.mobilePhoneNumber
      }
    });

    try {
      const response = await AttaServices.updatePhone(request);
      if (!response) {
        throw new Error("No response received from phone update API");
      }
      return true;
    } catch (error) {
      this.logError(error);
    }
    return false;
  }

  async handlePhoneConfirmationStart() {
    const request = new PhoneIntf.PhoneConfirmationStartRequest();
    request.sessionId = this.sessionId;
    request.phone = this.phoneData.formattedNumber;
    request.channel = "SMS";

    SentryPlugin.addBreadcrumb({
      category: 'Phone Update Modal',
      message: 'Starting phone confirmation',
      data: {
        phone: this.phoneData.formattedNumber
      }
    });

    try {
      const response = await PhoneConfirmationService.phoneConfirmationStart(request);
      if (!response) {
        throw new Error("No response received from phone confirmation API");
      }
      this.sessionId = response.sessionId;
      return true;
    } catch (error) {
      this.logError(error);
    }
    return false;
  }

  async handlePinCodeValidation() {
    const request = new PhoneIntf.PhoneConfirmationValidateRequest();
    request.sessionId = this.sessionId ?? "";
    request.token = this.verificationPinCode.join('');

    SentryPlugin.addBreadcrumb({
      category: 'Phone Update Modal',
      message: 'Validating entered pin code',
      data: {
        token: request.token
      }
    });

    try {
      const response = await PhoneConfirmationService.tokenValidatePhonePin(request);
      if (!response) {
        throw new Error("No response received from token validation API");
      }
      return response.success;
    } catch (error) {
      this.logError(error);
    }
    return false;
  }

  handlePinCodeChange(index: number, value: string) {
    if (!/^\d*$/.test(value)) return;

    const newCode = [...this.verificationPinCode];
    newCode[index] = value;
    this.verificationPinCode = newCode;

    if (value && index < 5) {
      const nextInput = document.getElementById(`code-${index + 1}`);
      nextInput?.focus();
    }
  }

  handleKeyDown(index: number, e: KeyboardEvent) {
    if (e.key === 'Backspace' && !this.verificationPinCode[index] && index > 0) {
      const prevInput = document.getElementById(`code-${index - 1}`);
      prevInput?.focus();
    }
  }

  handlePhoneInputUpdate(payload: {
    isValid: boolean;
    phone: string;
    formattedNumber: string;
    nationalNumber: string;
  }) {
    this.phoneData = payload;
  }

  logError(error: any) {
    if (import.meta.env.DEV) {
      console.error(error);
    } else {
      SentryPlugin.sendException(error);
    }
  }

  render() {
    return (
      <VDialog class="fade-in" persistent clipped fixed right temporary width="700" vModel={this.modalState}>
        <div class="phone-update-modal">
          <VCard elevation="0">
            <VCardTitle class="title-modal">
              {this.step === MODAL_STEP.PHONE_CONFIRMATION ? 'Atualize seu telefone de cadastro' : 'Verificação de telefone'}
            </VCardTitle>

            <VCardText class="pa-8">
              <form onSubmit={this.handleSubmit}>
                <VContainer class="pa-0">
                  {this.step === MODAL_STEP.PHONE_CONFIRMATION ? (
                    <div>
                      <VRow class="mb-6">
                        <VCol cols="12">
                          <h3 class="text-h5 font-weight-bold">O formato do login do iSolve irá mudar em breve</h3>
                          <p class="text--secondary text-body-1 mt-2">
                            Garanta que seu <span class="font-weight-medium">telefone</span> de cadastro está correto para que consiga realizar o login.
                          </p>
                        </VCol>
                      </VRow>

                      <VRow class="mb-2">
                        <VCol cols="12">
                          <VuePhoneNumberInput
                            v-on:update={this.handlePhoneInputUpdate}
                            translations={{
                              countrySelectorLabel: 'Código do país',
                              countrySelectorError: 'Escolha um país',
                              phoneNumberLabel: 'Número de telefone',
                              example: 'Exemplo :'
                            }}
                            default-country-code={'BR'}
                            v-model={this.phoneData.phone}
                            countries-height={24}
                            size="lg"
                          />
                        </VCol>
                      </VRow>

                      <VRow class="mb-1">
                        <VCol cols="12">
                          <VCheckbox
                            id="confirm"
                            label="Eu confirmo que meu telefone de cadastro está correto"
                            vModel={this.userConfirmedPhoneNumber}
                            class="mt-0"
                            hide-details
                          />
                        </VCol>
                      </VRow>

                      <VRow>
                        <VCol cols="12">
                          <VCheckbox
                            id="whatsapp"
                            label="Aceito receber SMS no meu número para verificar meu telefone"
                            vModel={this.smsConsent}
                            class="mt-0"
                            hide-details
                          />
                        </VCol>
                      </VRow>
                    </div>
                  ) : (
                    <div>
                      <VRow class="mb-6">
                        <VCol cols="12" >
                          <h3 class="text-h5 font-weight-bold">Digite o código de verificação</h3>
                          <p class="text--secondary text-body-1 mt-2">
                            Enviamos um código de 6 dígitos para o seu número. Digite-o abaixo para confirmar seu número.
                          </p>
                        </VCol>
                      </VRow>

                      <VRow class="mb-6 justify-center">
                        {this.verificationPinCode.map((digit, index) => (
                          <VCol cols="1" key={index} class="px-1">
                            <VTextField
                              id={`code-${index}`}
                              type="text"
                              maxlength={1}
                              value={digit}
                              onInput={(value: string) => this.handlePinCodeChange(index, value)}
                              onKeydown={(e: KeyboardEvent) => this.handleKeyDown(index, e)}
                              class="text-center text-h4"
                              outlined
                              hide-details
                            />
                          </VCol>
                        ))}
                      </VRow>

                      <VRow>
                        <VCol cols="12" class="text-center">
                          <p class="text--disabled">
                            Não recebeu o código? <VBtn onClick={this.handlePhoneConfirmationStart} text color="primary" class="ma-0 pa-0">Reenviar</VBtn>
                          </p>
                        </VCol>
                      </VRow>
                    </div>
                  )}

                  <VRow class="mt-8">
                    {this.step === MODAL_STEP.TOKEN_VALIDATION && (
                      <VCol cols="auto">
                        <VBtn
                          text
                          color="primary"
                          onClick={() => this.step = MODAL_STEP.PHONE_CONFIRMATION}
                          class="px-8 py-3"
                        >
                          Voltar
                        </VBtn>
                      </VCol>
                    )}
                    <VSpacer />
                    <VCol cols="auto">
                      <VBtn
                        type="submit"
                        color="primary"
                        depressed
                        large
                        disabled={this.step === MODAL_STEP.PHONE_CONFIRMATION ? !this.canSubmitPhoneConfirmationStep : !this.canSubmitTokenValidationStep}
                        class="px-8 py-3"
                      >
                        {this.step === MODAL_STEP.PHONE_CONFIRMATION ? 'Próximo' : 'Confirmar'}
                      </VBtn>
                    </VCol>
                  </VRow>
                </VContainer>
              </form>
            </VCardText>
          </VCard>
        </div>
      </VDialog>
    )
  }
};

export default PhoneUpdateModal;
